import Phaser from 'phaser';
import { COLORS } from '../style.js';

// Sit above selection rings (which jump to depth 100). The arrow goes
// one notch higher than the reticle so its head is never clipped at
// the seam where the two graphics meet.
const VFX_RETICLE_DEPTH = 150;
const VFX_ARROW_DEPTH = 160;

// Scope/reticle geometry around the defender tile. Tile is 80×80 so
// the outer ring at 58 sits just outside the tile corners; the inner
// ring at 30 hugs the unit number without crowding it. The 4 cardinal
// ticks float just outside the outer ring pointing inward — the
// classic "this is being targeted" cue.
const RETICLE_OUTER_R = 58;
const RETICLE_INNER_R = 30;
const RETICLE_TICK_OUTER = 70;
const RETICLE_TICK_INNER = 60;
const RETICLE_OUTER_THICKNESS = 4;
const RETICLE_INNER_THICKNESS = 3;
const RETICLE_TICK_THICKNESS = 4;

const ARROW_LINE_WIDTH = 5;
const ARROW_HEAD_LEN = 16;
const ARROW_HEAD_HALF_ANGLE = Math.PI / 6; // 30°
// Max inset on each end of the arrow. Scaled down for short attacks
// (within-region adjacencies, ~80px center-to-center) so the line
// always has visible length without overshooting the tiles.
const ARROW_INSET_MAX = 44;
const ARROW_INSET_RATIO = 0.35; // fraction of total distance per side

const PULSE_DURATION_MS = 360;

/**
 * Combat highlight overlay: a pulsing red target reticle around the
 * defending tile plus a red arrow from attacker to defender. Lives
 * only during the dice animation — `show` builds the graphics, `hide`
 * tears them down. Lazy: nothing exists between rolls.
 */
export class CombatVfx {
  private readonly scene: Phaser.Scene;
  private reticle: Phaser.GameObjects.Graphics | undefined;
  private arrow: Phaser.GameObjects.Graphics | undefined;
  private pulseTween: Phaser.Tweens.Tween | undefined;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  show(
    attacker: { readonly x: number; readonly y: number },
    defender: { readonly x: number; readonly y: number }
  ): void {
    this.hide();

    this.reticle = this.drawReticle(defender);
    this.reticle.setDepth(VFX_RETICLE_DEPTH);

    this.arrow = this.drawArrow(attacker, defender);
    this.arrow.setDepth(VFX_ARROW_DEPTH);

    // Single shared pulse: reticle and arrow breathe in lockstep.
    this.pulseTween = this.scene.tweens.add({
      targets: [this.reticle, this.arrow],
      alpha: { from: 1, to: 0.35 },
      duration: PULSE_DURATION_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  hide(): void {
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = undefined;
    }
    this.reticle?.destroy();
    this.arrow?.destroy();
    this.reticle = undefined;
    this.arrow = undefined;
  }

  private drawReticle(center: {
    readonly x: number;
    readonly y: number;
  }): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const RED = COLORS.attackTargetStrokeInt;

    g.lineStyle(RETICLE_OUTER_THICKNESS, RED, 1);
    g.strokeCircle(center.x, center.y, RETICLE_OUTER_R);

    g.lineStyle(RETICLE_INNER_THICKNESS, RED, 1);
    g.strokeCircle(center.x, center.y, RETICLE_INNER_R);

    g.lineStyle(RETICLE_TICK_THICKNESS, RED, 1);
    // 4 cardinal ticks: short radial strokes from just outside the
    // outer ring pointing inward to it. The gap reads as "the reticle
    // is locking on" rather than a continuous outline.
    const tickAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    for (const a of tickAngles) {
      const cx = Math.cos(a);
      const cy = Math.sin(a);
      g.beginPath();
      g.moveTo(
        center.x + cx * RETICLE_TICK_OUTER,
        center.y + cy * RETICLE_TICK_OUTER
      );
      g.lineTo(
        center.x + cx * RETICLE_TICK_INNER,
        center.y + cy * RETICLE_TICK_INNER
      );
      g.strokePath();
    }
    return g;
  }

  private drawArrow(
    from: { readonly x: number; readonly y: number },
    to: { readonly x: number; readonly y: number }
  ): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    g.lineStyle(ARROW_LINE_WIDTH, COLORS.attackTargetStrokeInt, 1);

    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1) return g; // identical positions — degenerate, no draw

    const ux = dx / dist;
    const uy = dy / dist;

    // Scale the inset on short adjacencies so the arrow always has a
    // visible line. Within-region tiles sit 80px apart so a fixed
    // 44px inset on each side would leave nothing to draw.
    const inset = Math.min(ARROW_INSET_MAX, dist * ARROW_INSET_RATIO);
    const sx = from.x + ux * inset;
    const sy = from.y + uy * inset;
    const ex = to.x - ux * inset;
    const ey = to.y - uy * inset;

    g.beginPath();
    g.moveTo(sx, sy);
    g.lineTo(ex, ey);
    g.strokePath();

    // Arrowhead — two short strokes back from the tip at ±30°.
    const angle = Math.atan2(uy, ux);
    g.beginPath();
    g.moveTo(ex, ey);
    g.lineTo(
      ex - ARROW_HEAD_LEN * Math.cos(angle - ARROW_HEAD_HALF_ANGLE),
      ey - ARROW_HEAD_LEN * Math.sin(angle - ARROW_HEAD_HALF_ANGLE)
    );
    g.moveTo(ex, ey);
    g.lineTo(
      ex - ARROW_HEAD_LEN * Math.cos(angle + ARROW_HEAD_HALF_ANGLE),
      ey - ARROW_HEAD_LEN * Math.sin(angle + ARROW_HEAD_HALF_ANGLE)
    );
    g.strokePath();

    return g;
  }
}
